import { zodResolver } from "@hookform/resolvers/zod";
import { MutableRefObject, UIEvent, useRef, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";

import type {
  IUnlockEligibleRecord,
  IUnlockStoreOption,
  Pager,
  UnlockStoreScope,
  UseUnlockRequestForm,
} from "@/@types/unlock-request";
import { toast } from "@/components/ui/use-toast";
import { UNLOCK_MODAL_LABELS, UNLOCK_TOASTS } from "@/constants/messages";
import { DEFAULT_PAGE } from "@/constants/pagination";
import {
  UNLOCK_ELIGIBLE_PAGE_SIZE,
  UNLOCK_LOAD_MORE_THRESHOLD_PX,
  UNLOCK_SEARCH_DEBOUNCE_MS,
  UNLOCK_STORE_SCOPE,
} from "@/constants/unlock-request";
import { UnlockEntityTypeEnum } from "@/enum/access_request";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  useCreateUnlockRequestMutation,
  useGetUnlockEligibleQuery,
  useGetUnlockStoresQuery,
} from "@/services/request-access";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import {
  isUnlockReleasable,
  resolveListState,
  resolveStoreScope,
  unlockRecordKey,
  unlockRecordName,
} from "@/utils/unlock-request";

import {
  UnlockRequestFormSchema,
  UnlockRequestFormValues,
} from "./validate-form";

export function useUnlockRequestForm(): UseUnlockRequestForm {
  const { handleModal } = useDialogModal();
  const submittingRef: MutableRefObject<boolean> = useRef<boolean>(false);
  const [picked, setPicked] = useState<IUnlockEligibleRecord | null>(null);

  const form: UseFormReturn<UnlockRequestFormValues> =
    useForm<UnlockRequestFormValues>({
      resolver: zodResolver(UnlockRequestFormSchema),
      mode: "onChange",
      defaultValues: {
        entityType: UnlockEntityTypeEnum.OPERATOR,
        storeCode: "",
        search: "",
        entityCode: "",
        comment: "",
      },
    });

  const entityType: UnlockEntityTypeEnum = form.watch("entityType");
  const chosenStore: string = form.watch("storeCode");
  const entityCode: string = form.watch("entityCode");
  const debouncedSearch: string = useDebouncedValue(
    form.watch("search"),
    UNLOCK_SEARCH_DEBOUNCE_MS,
  );

  const {
    data: storeOptions,
    isLoading: isLoadingStores,
    isError: isStoresError,
  } = useGetUnlockStoresQuery();

  const stores: IUnlockStoreOption[] = storeOptions ?? [];
  const storeScope: UnlockStoreScope = resolveStoreScope(
    isLoadingStores,
    isStoresError,
    stores.length,
  );

  const effectiveStore: string =
    storeScope === UNLOCK_STORE_SCOPE.SINGLE ? stores[0].value : chosenStore;

  const listKey: string = `${entityType}|${effectiveStore}|${debouncedSearch}`;
  const [pager, setPager] = useState<Pager>({ key: listKey, page: DEFAULT_PAGE });
  const page: number = pager.key === listKey ? pager.page : DEFAULT_PAGE;

  const {
    currentData,
    isFetching: isFetchingRecords,
    isError: isRecordsError,
  } = useGetUnlockEligibleQuery(
    {
      entityType,
      storeCode: Number(effectiveStore),
      search: debouncedSearch || undefined,
      page,
      limit: UNLOCK_ELIGIBLE_PAGE_SIZE,
    },
    { skip: !effectiveStore },
  );

  const [createUnlockRequest, { isLoading: isSubmitting }] =
    useCreateUnlockRequestMutation();

  const records: IUnlockEligibleRecord[] = currentData?.data ?? [];
  const totalCount: number = currentData?.meta?.total ?? records.length;
  const hasMore: boolean = currentData?.meta?.next != null;
  const isLoadingMore: boolean = isFetchingRecords && page > DEFAULT_PAGE;

  const selected: IUnlockEligibleRecord | null =
    picked && unlockRecordKey(picked) === entityCode ? picked : null;

  const close = (): void => {
    handleModal({ isOpen: false });
  };

  const submit = async (values: UnlockRequestFormValues): Promise<void> => {
    if (!selected || submittingRef.current) return;
    submittingRef.current = true;

    try {
      await createUnlockRequest({
        storeCode: selected.storeCode,
        entityType: values.entityType,
        entityCode: unlockRecordKey(selected),
        comment: values.comment.trim() || undefined,
      }).unwrap();

      toast(UNLOCK_TOASTS.CREATED(unlockRecordName(selected)));
      close();
    } catch (error) {
      toast(
        UNLOCK_TOASTS.CREATE_FAILED(
          extractRtkErrorMessage(
            error,
            UNLOCK_MODAL_LABELS.CREATE_FALLBACK_ERROR,
          ),
        ),
      );
    } finally {
      submittingRef.current = false;
    }
  };

  const clearTarget = (): void => {
    setPicked(null);
    form.setValue("entityCode", "", { shouldValidate: true });
  };

  const pickTarget = (record: IUnlockEligibleRecord): void => {
    setPicked(record);
    form.setValue("entityCode", unlockRecordKey(record), {
      shouldValidate: true,
    });
  };

  const changeEntityType = (next: UnlockEntityTypeEnum): void => {
    form.setValue("entityType", next);
    clearTarget();
  };

  const changeStore = (next: string): void => {
    form.setValue("storeCode", next);
    clearTarget();
  };

  const onListScroll = (event: UIEvent<HTMLDivElement>): void => {
    const target: HTMLDivElement = event.currentTarget;
    const nearBottom: boolean =
      target.scrollTop + target.clientHeight >=
      target.scrollHeight - UNLOCK_LOAD_MORE_THRESHOLD_PX;

    if (!nearBottom || !hasMore || isFetchingRecords) return;

    setPager({ key: listKey, page: page + 1 });
  };

  return {
    form,
    stores,
    storeScope,
    listState: resolveListState({
      storeScope,
      hasStore: !!effectiveStore,
      isLoadingRecords: isFetchingRecords && records.length === 0,
      isRecordsError,
      total: records.length,
    }),
    selectedStore:
      stores.find(
        (option: IUnlockStoreOption): boolean => option.value === chosenStore,
      ) ?? null,
    records,
    selected,
    shownCount: records.length,
    totalCount,
    isLoadingMore,
    canConfirm:
      form.formState.isValid &&
      !!selected &&
      isUnlockReleasable(selected) &&
      !isSubmitting,
    isSubmitting,
    onSubmit: form.handleSubmit(submit),
    onListScroll,
    changeEntityType,
    changeStore,
    pickTarget,
    close,
  };
}
